import { __decorate } from "tslib";
import { Component, Vue } from "vue-property-decorator";
import { askToDiscardChanges, registerUnsavedForm } from "@/utilities/unsavedChanges";
/**
 * "You have unsaved changes" protection for a form.
 *
 * The form overrides `unsavedState()` to return the values that count as its edits. A
 * snapshot is taken when it mounts; while the current state differs from it:
 *  - `confirmLeave()` (call it from Cancel / close) asks before resolving true,
 *  - any route change asks first (Cancel links, back chevron, breadcrumbs, sidebar, browser back),
 *  - refresh / closing the tab shows the browser's leave-page prompt.
 * Call `skipUnsavedCheck()` right before navigating away after a successful save.
 */
let UnsavedChangesMixin = /** @class */ (() => {
    let UnsavedChangesMixin = class UnsavedChangesMixin extends Vue {
        constructor() {
            super(...arguments);
            // A SweetAlert is open - b-modal forms must not pull focus back from it (:no-enforce-focus)
            this.confirmingDiscard = false;
            this.unsavedSnapshot = null;
            this.unsavedCheckSkipped = false;
            this.removeUnsavedHooks = [];
        }
        unsavedState() {
            return null;
        }
        markFormClean() {
            this.unsavedSnapshot = JSON.stringify(this.unsavedState());
            this.unsavedCheckSkipped = false;
        }
        skipUnsavedCheck() {
            this.unsavedCheckSkipped = true;
        }
        hasUnsavedChanges() {
            if (this.unsavedCheckSkipped || this.unsavedSnapshot === null) {
                return false;
            }
            return JSON.stringify(this.unsavedState()) !== this.unsavedSnapshot;
        }
        // True when it's fine to leave: nothing changed, or the user chose to discard
        confirmLeave() {
            if (!this.hasUnsavedChanges()) {
                return Promise.resolve(true);
            }
            this.confirmingDiscard = true;
            return askToDiscardChanges().then((discard) => {
                this.confirmingDiscard = false;
                if (discard) {
                    this.skipUnsavedCheck();
                }
                return discard;
            });
        }
        mounted() {
            // After the form's own created/mounted set its starting values
            this.$nextTick(() => this.markFormClean());
            const onBeforeUnload = (e) => {
                if (this.hasUnsavedChanges()) {
                    e.preventDefault();
                    e.returnValue = "";
                }
            };
            window.addEventListener("beforeunload", onBeforeUnload);
            // Registered only while this form is open; the returned function removes it
            const removeRouteGuard = this.$router.beforeEach((to, from, next) => {
                if (!this.hasUnsavedChanges()) {
                    next();
                    return;
                }
                this.confirmLeave().then((leave) => (leave ? next() : next(false)));
            });
            this.removeUnsavedHooks = [
                () => window.removeEventListener("beforeunload", onBeforeUnload),
                removeRouteGuard,
                registerUnsavedForm({
                    hasUnsavedChanges: () => this.hasUnsavedChanges(),
                    skipUnsavedCheck: () => this.skipUnsavedCheck(),
                }),
            ];
        }
        beforeDestroy() {
            this.removeUnsavedHooks.forEach((remove) => remove());
            this.removeUnsavedHooks = [];
        }
    };
    UnsavedChangesMixin = __decorate([
        Component
    ], UnsavedChangesMixin);
    return UnsavedChangesMixin;
})();
export default UnsavedChangesMixin;
